// frontend/src/pages/BookingPage.jsx - FIXED VERSION
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useBooking } from '../context/BookingContext';
import { useAuth } from '../context/AuthContext';
import { bookingsAPI } from '../services/api';
import BookingForm from '../components/booking/BookingForm';
import PaymentForm from '../components/booking/PaymentForm';
import BookingConfirmation from '../components/booking/BookingConfirmation';
import Loading from '../components/common/Loading';
import ErrorMessage from '../components/common/ErrorMessage';

const BookingPage = () => {
  const { flightId } = useParams();
  const navigate = useNavigate();
  const { selectedFlight, searchParams } = useBooking();
  const { isAuthenticated } = useAuth();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [bookingFormData, setBookingFormData] = useState(null); // Renamed for clarity
  const [paymentFormData, setPaymentFormData] = useState(null); // Renamed for clarity
  const [confirmedBooking, setConfirmedBooking] = useState(null);
  const [createdBooking, setCreatedBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!selectedFlight) {
      navigate('/search');
      return;
    }
  }, [selectedFlight, navigate]);

  const handleBookingSubmit = (data) => {
    console.log('📝 Booking form submitted:', data);
    setBookingFormData(data);
    setCurrentStep(2);
  };

  const handlePaymentSubmit = async (paymentData) => {
    console.log('💳 Payment form submitted:', paymentData);
    setPaymentFormData(paymentData);
    setLoading(true);
    setError('');

    try {
      // Helper function to convert date to datetime format
      const formatDateOfBirth = (dateString) => {
        // If it's already a datetime, return as is
        if (dateString.includes('T') || dateString.includes(' ')) {
          return dateString;
        }
        // Convert YYYY-MM-DD to YYYY-MM-DDTHH:MM:SS format
        return dateString + 'T00:00:00';
      };

      // Combine all the data before sending to API
      const completeBookingData = {
        // Flight information
        flight_id: selectedFlight.id,
        
        // Passenger and contact information (with fixed date format)
        passengers: bookingFormData.passengers.map(passenger => ({
          ...passenger,
          date_of_birth: formatDateOfBirth(passenger.date_of_birth)
        })),
        contact_email: bookingFormData.contact_email,
        contact_phone: bookingFormData.contact_phone,
        special_requests: bookingFormData.special_requests || '',
        
        // Payment information
        payment_method: paymentData.payment_method,
        card_number: paymentData.card_number,
        card_holder: paymentData.card_holder,
        expiry_date: paymentData.expiry_date,
        cvv: paymentData.cvv
      };

      console.log('🚀 Creating booking with complete data:', completeBookingData);
      
      // Step 1: Create booking in backend
      const bookingResponse = await bookingsAPI.createBooking(completeBookingData);
      console.log('✅ Booking created:', bookingResponse);
      
      setCreatedBooking(bookingResponse);

      // Step 2: Process payment (mock for now, but could be real Stripe/PayPal)
      console.log('💰 Processing payment...');
      
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      // Step 3: Confirm payment in backend
      const paymentConfirmation = {
        payment_intent_id: `pi_${Date.now()}`,
        payment_method: 'stripe',
        transaction_id: `txn_${Date.now()}`,
        amount_paid: bookingResponse.total_amount,
        currency: 'USD',
        payment_status: 'succeeded',
        payment_timestamp: new Date().toISOString()
      };

      console.log('🔄 Confirming payment...');
      const paymentResult = await bookingsAPI.confirmPayment(
        bookingResponse.id, 
        paymentConfirmation
      );
      console.log('✅ Payment confirmed:', paymentResult);

      // Step 4: Get updated booking details
      const finalBooking = await bookingsAPI.getBookingDetails(bookingResponse.id);
      console.log('📄 Final booking:', finalBooking);

      setConfirmedBooking({
        ...finalBooking,
        flight: selectedFlight,
        passengers: bookingFormData.passengers
      });
      setCurrentStep(3);
      
    } catch (err) {
      console.error('❌ Booking/Payment failed:', err);
      setError(err.message || 'Booking failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    if (loading) {
      return <Loading message="Processing your booking and payment..." />;
    }

    switch (currentStep) {
      case 1:
        return (
          <BookingForm
            flight={selectedFlight}
            searchParams={searchParams}
            onSubmit={handleBookingSubmit}
          />
        );
      case 2:
        return (
          <PaymentForm
            bookingData={bookingFormData}
            flight={selectedFlight}
            onSubmit={handlePaymentSubmit}
            onBack={() => setCurrentStep(1)}
          />
        );
      case 3:
        return confirmedBooking ? (
          <BookingConfirmation booking={confirmedBooking} />
        ) : (
          <Loading message="Loading confirmation..." />
        );
      default:
        return null;
    }
  };

  if (!selectedFlight) {
    return <Loading message="Loading flight details..." />;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4">
              {[
                { step: 1, label: 'Booking Details' },
                { step: 2, label: 'Payment' },
                { step: 3, label: 'Confirmation' }
              ].map(({ step, label }) => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    step <= currentStep 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-gray-300 text-gray-600'
                  }`}>
                    {step}
                  </div>
                  <span className={`ml-2 text-sm ${
                    step <= currentStep ? 'text-blue-600' : 'text-gray-500'
                  }`}>
                    {label}
                  </span>
                  {step < 3 && (
                    <div className={`ml-4 w-8 h-px ${
                      step < currentStep ? 'bg-blue-600' : 'bg-gray-300'
                    }`} />
                  )}
                </div>
              ))}
            </div>
          </div>

          {error && (
            <div className="mb-6">
              <ErrorMessage message={error} />
            </div>
          )}

          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default BookingPage;